/**
 * The Note menu: what a note is.
 *
 * It is the largest menu and one of the two that grow, so it sits in its own file and is
 * pulled into the bar by `score-menus.ts`; what is written on a *bar* is next door in
 * `score-menus-bars.ts`. This menu talks to note entry, which is how the keyboard writes
 * notes as well, so a duration picked here and a duration typed with a digit are the same
 * state. Everything else builds a command over the selection.
 */

import { engrave, marks, ossia, selection as select, voices } from "@app/score-editor";
import type { Articulation, BeamState, NoteType } from "@app/score-editor";
import type { ScoreActions } from "./score-actions";
import { RULE } from "./score-menu-model";
import type { Menu, MenuEntry } from "./score-menu-model";
import { keyboardItems } from "./score-menus-midi";

/**
 * The note values on offer, longest first: the digit that types each one, and what it looks
 * like. `flags` of -1 is a semibreve, which has no stem at all.
 */
export const DURATIONS: readonly {
  type: NoteType;
  label: string;
  key: string;
  flags: number;
  filled: boolean;
}[] = [
  { type: "whole", label: "Semibreve", key: "1", flags: -1, filled: false },
  { type: "half", label: "Minim", key: "2", flags: 0, filled: false },
  { type: "quarter", label: "Crotchet", key: "3", flags: 0, filled: true },
  { type: "eighth", label: "Quaver", key: "4", flags: 1, filled: true },
  { type: "16th", label: "Semiquaver", key: "5", flags: 2, filled: true },
  { type: "32nd", label: "Demisemiquaver", key: "6", flags: 3, filled: true },
];

export const ARTICULATIONS: readonly { mark: Articulation; label: string }[] = [
  { mark: "staccato", label: "Staccato" },
  { mark: "staccatissimo", label: "Staccatissimo" },
  { mark: "accent", label: "Accent" },
  { mark: "strong-accent", label: "Marcato" },
  { mark: "tenuto", label: "Tenuto" },
  { mark: "detached-legato", label: "Portato" },
  { mark: "breath-mark", label: "Breath mark" },
  { mark: "harmonic", label: "Harmonic" },
];

const BEAMS: readonly { state: BeamState; label: string }[] = [
  { state: "auto", label: "Beam as the metre asks" },
  { state: "begin", label: "Beam the selection together" },
  { state: "none", label: "Beam nothing" },
];

/**
 * The four directions a selected note or rest can be nudged, and the arrow that does it.
 *
 * `x` and `y` are steps rather than distances: the size of a step belongs to the editing
 * layer, which is also what the arrow keys use, so the menu and the keyboard cannot drift
 * apart. `y` counts upwards, as the document does.
 */
const NUDGES: readonly { label: string; hint: string; x: number; y: number }[] = [
  { label: "Move it up", hint: "⌘↑", x: 0, y: 1 },
  { label: "Move it down", hint: "⌘↓", x: 0, y: -1 },
  { label: "Move it left", hint: "⌘←", x: -1, y: 0 },
  { label: "Move it right", hint: "⌘→", x: 1, y: 0 },
];

/**
 * How many voices the caret’s staff is written in, in the caret’s bar, or `0` for no caret.
 *
 * A bar rather than the whole staff, because that is what a voice belongs to: a divided
 * passage is divided only where it is divided.
 */
function voicesHere(actions: ScoreActions): number {
  const caret = actions.caret;
  if (caret === null) return 0;
  const part = actions.score.parts.find((candidate) => candidate.id === caret.partId);
  const measure = part?.measures[caret.measureId];
  if (measure === undefined) return 0;
  const standing = voices.standingIn(measure, caret.voiceIndex);
  return standing === null ? 0 : voices.voicesOn(measure, standing.staff).length;
}

/** How many staves the caret’s part is written on, or `0` for no caret. */
function stavesHere(actions: ScoreActions): number {
  const caret = actions.caret;
  if (caret === null) return 0;
  return actions.score.parts.find((candidate) => candidate.id === caret.partId)?.staves.length ?? 0;
}

/** What a note is: how long, what is written on it, and how it joins its neighbours. */
export function noteMenu(actions: ScoreActions): Menu {
  const { score, selection, entry } = actions;
  const nothing = selection.events.size === 0;
  // Cue is a setting rather than a verb, so it is ticked when the whole selection is one --
  // which is also when running it takes the passage back into the part's own music.
  const chosen = select.selectedLocations(score, selection);
  const cued = chosen.length > 0 && chosen.every((at) => at.event.cue);
  // Putting something back is only worth offering where something was moved, and the
  // selection knows: an event that was never nudged carries no offset at all.
  const moved = chosen.some((at) => at.event.offset !== null);
  // A staff keeps one voice, so there is nothing to take out until there are two, and it
  // will not hold more than four. Both are read where the caret stands, which is the staff
  // either item would act on.
  const standing = voicesHere(actions);
  // An ossia is one per staff per bar, so offering one and taking it away are the two
  // halves of the same switch, read where the caret stands.
  const offered = ossia.offeredOn(actions.score, actions.caret);
  // Nothing can be sent across a part that has one staff, and there is nothing to bring
  // home until something has been sent.
  const staves = stavesHere(actions);
  const crossed = chosen.some((at) => at.event.staff !== null);

  const durations: MenuEntry[] = DURATIONS.map((duration) => ({
    label: duration.label,
    hint: duration.key,
    checked: entry.duration.type === duration.type,
    run: () => actions.type({ kind: "duration", type: duration.type }),
  }));

  const articulations: MenuEntry[] = ARTICULATIONS.map((articulation) => ({
    label: articulation.label,
    disabled: nothing,
    run: () => actions.edit(marks.articulate(score, selection, articulation.mark)),
  }));

  return {
    title: "Note",
    items: [
      ...durations,
      RULE,
      {
        label: "Dotted",
        hint: ".",
        checked: entry.duration.dots > 0,
        run: () => actions.type({ kind: "dots", dots: entry.duration.dots > 0 ? 0 : 1 }),
      },
      {
        label: "Triplet",
        hint: "/",
        checked: entry.duration.tuplet !== null,
        run: () =>
          actions.type({
            kind: "tuplet",
            ratio: entry.duration.tuplet === null ? { actual: 3, normal: 2 } : null,
          }),
      },
      { label: "Rest", hint: "R", run: () => actions.type({ kind: "rest" }) },
      { label: "Tie to the next note", hint: "T", run: () => actions.type({ kind: "tie" }) },
      {
        label: "Untie",
        disabled: nothing,
        run: () => actions.edit(marks.untie(score, selection)),
      },
      RULE,
      ...articulations,
      {
        label: "No articulation",
        disabled: nothing,
        run: () => actions.edit(marks.clearArticulations(score, selection)),
      },
      RULE,
      {
        label: "Stems up",
        disabled: nothing,
        run: () => actions.edit(marks.stem(score, selection, "up")),
      },
      {
        label: "Stems down",
        disabled: nothing,
        run: () => actions.edit(marks.stem(score, selection, "down")),
      },
      {
        label: "Stems as the engraver asks",
        disabled: nothing,
        run: () => actions.edit(marks.stem(score, selection, "auto")),
      },
      ...BEAMS.map((beam) => ({
        label: beam.label,
        disabled: nothing,
        run: () => actions.edit(marks.beam(score, selection, beam.state)),
      })),
      RULE,
      {
        label: "Write grace notes",
        hint: "S",
        checked: entry.grace === "acciaccatura",
        run: () =>
          actions.type({
            kind: "grace",
            grace: entry.grace === "acciaccatura" ? null : "acciaccatura",
          }),
      },
      {
        label: "Write grace notes, on the beat",
        hint: "⇧S",
        checked: entry.grace === "appoggiatura",
        run: () =>
          actions.type({
            kind: "grace",
            grace: entry.grace === "appoggiatura" ? null : "appoggiatura",
          }),
      },
      {
        label: "Selection as grace notes",
        disabled: nothing,
        run: () => actions.edit(marks.grace(score, selection, "acciaccatura")),
      },
      {
        label: "Selection as grace notes, on the beat",
        disabled: nothing,
        run: () => actions.edit(marks.grace(score, selection, "appoggiatura")),
      },
      {
        label: "Selection as ordinary notes",
        disabled: nothing,
        run: () => actions.edit(marks.grace(score, selection, null)),
      },
      RULE,
      {
        label: "Cue",
        disabled: nothing,
        checked: cued,
        run: () => actions.edit(marks.cue(score, selection)),
      },
      RULE,
      ...NUDGES.map((nudge) => ({
        label: nudge.label,
        hint: nudge.hint,
        disabled: nothing,
        run: () => actions.nudge(nudge.x * engrave.NUDGE_STEP, nudge.y * engrave.NUDGE_STEP),
      })),
      {
        label: "Put it back where it was",
        disabled: !moved,
        run: () => actions.unnudge(),
      },
      RULE,
      {
        label: "Another voice on this staff",
        hint: "V",
        disabled: standing >= voices.MAX_VOICES,
        run: () => actions.addVoice(),
      },
      {
        label: "Take this voice off the staff",
        hint: "⇧V",
        disabled: standing < 2,
        run: () => actions.removeVoice(),
      },
      {
        label: "Offer an ossia above this staff",
        disabled: actions.caret === null || offered,
        run: () => actions.addOssia(),
      },
      {
        label: "Take the ossia away",
        disabled: !offered,
        run: () => actions.removeOssia(),
      },
      RULE,
      {
        label: "Draw it on the staff above",
        hint: "⌘⇧↑",
        disabled: nothing || staves < 2,
        run: () => actions.crossStaff(-1),
      },
      {
        label: "Draw it on the staff below",
        hint: "⌘⇧↓",
        disabled: nothing || staves < 2,
        run: () => actions.crossStaff(1),
      },
      {
        label: "Draw it on its own staff again",
        disabled: !crossed,
        run: () => actions.uncrossStaff(),
      },
      ...keyboardItems(actions.keyboard),
    ],
  };
}
